import { httpRequest } from '@/shared/lib/http'

/**
 * Bandeja de cierre de RH (SRH-304).
 *
 * Es el paso que antes no existía: la aprobación del solicitante ya no cierra
 * el proceso, deja al candidato aquí, y RH decide si lo selecciona o lo manda a
 * Dirección; solo descarta lo que Dirección rechazó.
 */

/** Las pestañas de la bandeja, que son estatus del candidato. */
export type EstatusDeCierre =
  | 'pendiente_cierre'
  | 'pendiente_direccion'
  | 'decision_direccion'
  | 'seleccionado'

export interface CandidatoDeCierre {
  candidateId: string
  fullName: string
  /** Llega nulo en los candidatos que se dieron de alta sin correo. */
  email: string | null
  phone: string | null
  status: EstatusDeCierre
  jobRequest: {
    id: number
    position: string
    area: string
  } | null
  /** Nulo cuando no hay aprobación registrada del solicitante. */
  requesterApproval: {
    userName: string
    /** Puede venir sin zona: formatear con los helpers de `dateUtils`. */
    at: string
  } | null
  /**
   * Solo con la decisión de Dirección recibida (SRH-311): qué decidió, por qué,
   * quién y cuándo. Dirección ya no cierra; RH confirma.
   */
  directionDecision?: {
    result: 'approved' | 'rejected'
    reason: string | null
    comment: string | null
    userName: string | null
    at: string
  } | null
  /**
   * Solo en los seleccionados. `role` llega nulo en los que ya estaban
   * seleccionados antes de RH-03.1, cuando no se registraba quién decidía.
   */
  finalDecision: {
    role: 'rh' | 'direction' | null
    userName: string
    at: string
  } | null
}

const ruta = (id: string, accion: string) =>
  `/candidates/profile/${id}/closing/${accion}`

export const bandejaDeCierre = {
  /** Una pestaña. Cualquier otro estatus responde 400. */
  async listar(status: EstatusDeCierre): Promise<CandidatoDeCierre[]> {
    const lista = await httpRequest<CandidatoDeCierre[]>(
      `/candidates/closing?status=${status}`,
    )
    return lista ?? []
  },

  /** Cierra el proceso: el candidato queda seleccionado. */
  async seleccionar(candidateId: string, confirmOverbooking = false): Promise<void> {
    await httpRequest<unknown>(ruta(candidateId, 'select'), {
      method: 'PATCH',
      // Con la vacante ya cubierta el servidor advierte (409); aceptar es
      // reintentar con esto (SRH-313).
      ...(confirmOverbooking
        ? { body: JSON.stringify({ confirmOverbooking: true }) }
        : {}),
    })
  },

  /** Lo manda a validación de Dirección; ahí se decide. */
  async enviarADireccion(candidateId: string): Promise<void> {
    await httpRequest<unknown>(
      ruta(candidateId, 'send-to-direction'),
      { method: 'PATCH' },
    )
  },

  /** Descarte del catálogo de motivos. El motivo es obligatorio. */
  async descartar(
    candidateId: string,
    datos: { reason: string; comment?: string },
  ): Promise<void> {
    await httpRequest<unknown>(ruta(candidateId, 'discard'), {
      method: 'PATCH',
      body: JSON.stringify(datos),
    })
  },
}
